import React, { useEffect, useState } from "react";
import withAuth from "../withAuth";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Card, CardBody, Col, Container, Row, Button, Input, Label } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../Network";
import { isMarketerReadOnly } from "../../helpers/adminRole";
import { handleAuthError } from "../../utils";
import { toast, ToastContainer } from "react-toastify";

const ACCESS_OPTIONS = [
  { key: "live_interaction_class", title: "Live Interaction Class", note: "Teacher-led classes with structured weekly sessions." },
  { key: "ai_tutor", title: "AI Tutor", note: "Flexible self-paced learning with AI guidance." },
  { key: "cbt", title: "CBT", note: "Computer-based testing and assessment workflows." },
];

const PartnerAccess = () => {
  const readOnly = isMarketerReadOnly();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState(null);
  const [accessMap, setAccessMap] = useState({});

  useEffect(() => {
    document.title = "Manage Partner Access | RYD Admin";
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accessRes, dashboardRes] = await Promise.all([
        axios.get(`${baseUrl}/admin/partner/access/${id}`),
        axios.get(`${baseUrl}/admin/partner/dashboard/${id}`),
      ]);
      const list = Array.isArray(accessRes?.data?.data) ? accessRes.data.data : [];
      const map = {};
      list.forEach((row) => {
        map[row.accessType] = { selected: true, isActive: row.isActive !== false };
      });
      setAccessMap(map);
      setPartner(dashboardRes?.data?.data?.partner || null);
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to load partner access");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (type) => {
    setAccessMap((prev) => {
      const current = prev[type] || { selected: false, isActive: true };
      return {
        ...prev,
        [type]: {
          selected: !current.selected,
          isActive: !current.selected ? true : current.isActive,
        },
      };
    });
  };

  const toggleActive = (type) => {
    setAccessMap((prev) => {
      const current = prev[type] || { selected: false, isActive: true };
      if (!current.selected) return prev;
      return {
        ...prev,
        [type]: {
          ...current,
          isActive: !current.isActive,
        },
      };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const accesses = ACCESS_OPTIONS
        .filter((opt) => accessMap[opt.key]?.selected)
        .map((opt) => ({
          accessType: opt.key,
          isActive: accessMap[opt.key]?.isActive !== false,
        }));
      await axios.put(`${baseUrl}/admin/partner/access/${id}`, { accesses });
      toast.success("Partner access updated successfully");
      await fetchData();
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to update partner access");
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="align-items-center">
            <Col md={8}>
              <Breadcrumbs title="Manage Partner" breadcrumbItem={`${partner?.organizationName || "Partner"} Access`} />
            </Col>
            <Col md={4} className="text-md-end mb-3">
              <Link to={`/partner/dashboard/${id}`} className="btn btn-light">
                <i className="mdi mdi-arrow-left me-1" />
                Back to Dashboard
              </Link>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center mt-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Row>
              {ACCESS_OPTIONS.map((option) => {
                const state = accessMap[option.key] || { selected: false, isActive: true };
                return (
                  <Col xl={6} md={6} sm={12} key={option.key} className="mb-4">
                    <Card
                      className={`h-100 shadow-sm ${
                        state.selected ? "border border-primary" : "border"
                      }`}
                    >
                      <CardBody className="p-4 d-flex flex-column" style={{ minHeight: 220 }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h5 className="mb-0">{option.title}</h5>
                          <Button
                            color={state.selected ? "primary" : "light"}
                            size="sm"
                            onClick={() => !readOnly && toggleSelected(option.key)}
                            disabled={readOnly}
                          >
                            {state.selected ? "Selected" : "Select"}
                          </Button>
                        </div>
                        <p className="text-muted flex-grow-1 mb-4">{option.note}</p>
                        <div className="d-flex align-items-center gap-2 border-top pt-3 mt-auto">
                          <Input
                            id={`active-${option.key}`}
                            type="switch"
                            checked={state.selected && state.isActive}
                            disabled={readOnly || !state.selected}
                            onChange={() => !readOnly && toggleActive(option.key)}
                          />
                          <Label for={`active-${option.key}`} className="mb-0">
                            {state.selected ? (state.isActive ? "Active" : "Inactive") : "Not selected"}
                          </Label>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {!readOnly && (
          <div className="d-flex justify-content-end">
            <Button color="primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? "Saving..." : "Save Access"}
            </Button>
          </div>
          )}
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(PartnerAccess);
